'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import type { TDynamicData } from '@eliadmin/core/src/api/dynamics/dynamics-api';

import { ProductBadge } from '@eliadmin/core/src/components/product/detail/product-badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { FaRegClock } from 'react-icons/fa';
import { MdOutlineStore } from 'react-icons/md';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { LuArrowUpRight } from 'react-icons/lu';
import { useEffect, useRef, useState } from 'react';

import { formatContactAddress, googleMapsSearchUrl } from '../../utils/contact-address';

/** Street View of the Prague store — relevant only to the Czech storefront. */
const STREET_VIEW_LOCALE = 'cs';
const STREET_VIEW_EMBED_URL =
  'https://www.google.com/maps/embed?pb=!4v1790087006756!6m8!1m7!1sCAoSF0NJSE0wb2dLRUlDQWdJRFJocFNrbFFF!2m2!1d50.07446513733882!2d14.40617411754672!3f320.04642468411777!4f-10.541825342844021!5f0.4000000000000002';

function StoreStreetView({ title }: { title: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="relative h-[260px] w-full rounded-[4px] bg-muted sm:h-[400px] md:h-auto md:max-w-[451px]"
    >
      {visible && (
        <iframe
          src={STREET_VIEW_EMBED_URL}
          title={title}
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 h-full w-full rounded-[4px] border-0"
        />
      )}
      <noscript>
        <iframe
          src={STREET_VIEW_EMBED_URL}
          title={title}
          loading="lazy"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 h-full w-full rounded-[4px] border-0"
        />
      </noscript>
    </div>
  );
}

export function Stores({
  stores,
  className,
  contactContrastApproved = false,
}: {
  stores: TDynamicData[];
  className?: { section?: string };
  /** Human-approved text-only contrast change on the Slovak contact page. */
  contactContrastApproved?: boolean;
}) {
  const { t } = useTranslations();
  const showStreetView = useLocaleStore(s => s.currentLocale) === STREET_VIEW_LOCALE;
  // The store's settings address, not the entry's "Adresa" field — see `formatContactAddress`.
  // The map link searches it too; the entry's "Google maps (URL)" is not read.
  const address = formatContactAddress(useGlobalStore(s => s.generalLayout)?.contact);

  return (
    <section id="predajna" className={cn('custom-section', 'generic-gap', className?.section)}>
      {stores.map(store => {
        const badge = store?.fields[0]?.value;
        const openingHours = store?.fields[2]?.value;
        const background = store?.fields[4]?.image?.url;

        return (
          <div
            key={store.name}
            className={cn(
              'custom-container',
              'flex flex-col-reverse items-center justify-center gap-3 sm:gap-4 md:flex-row md:items-stretch md:justify-between lg:gap-6',
            )}
          >
            {showStreetView && <StoreStreetView title={store.name} />}
            <div
              className={cn(
                'relative flex h-[500px] w-full flex-col items-start justify-between rounded-[4px] p-3 sm:p-4 lg:p-6',
                showStreetView && 'md:max-w-[925px]',
              )}
            >
              {!!badge && (
                <ProductBadge
                  text={badge}
                  background="#F1E4DE"
                  color="#3E1C12"
                  className="relative z-10"
                />
              )}
              <div className="relative z-10 flex w-full flex-col items-start justify-start gap-4 sm:gap-5 lg:gap-6">
                <Heading title={store.name} color="white" />
                <div className="flex w-full flex-col items-start justify-start gap-[6px] sm:gap-2">
                  {!!address && (
                    <div className="flex w-full items-center justify-start gap-2 sm:gap-2.5">
                      <MdOutlineStore size={24} className="text-white" />
                      <Paragraph size="lg" color="white">
                        {address}
                      </Paragraph>
                    </div>
                  )}
                  {!!openingHours && (
                    <div className="flex w-full items-center justify-start gap-2 sm:gap-2.5">
                      <FaRegClock size={24} className="text-white" />
                      <Paragraph size="lg" color="white">
                        {openingHours}
                      </Paragraph>
                    </div>
                  )}
                </div>
                {!!address && (
                  <Button
                    asChild
                    variant={'secondary'}
                    className={contactContrastApproved ? 'text-[#806244] hover:text-white' : undefined}
                  >
                    <Link
                      prefetch={false}
                      href={googleMapsSearchUrl(address)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('text3729')} <LuArrowUpRight size={18} aria-hidden />
                    </Link>
                  </Button>
                )}
              </div>
              <div className="ea-categories-card__image-overlay absolute inset-0 z-[1] transition-opacity duration-300 group-hover:opacity-0" />
              <Image
                src={background ?? '/images/placeholder.png'}
                alt={resolveImageAlt(store?.fields?.[4]?.image, store?.name)}
                fill
                className="absolute inset-0 z-0 h-full w-full object-cover object-center"
              />
            </div>
          </div>
        );
      })}
    </section>
  );
}
